import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from 'react-router-dom';

export const AuthContext = createContext({
  user: null,
  login: () => {},
  logout: () => {},
  loading: true,
  loginLoading: false,
  loginProgress: 0,
  loginMessage: '',
  currentDomain: '',
  activeDomainConfig: null,
  matchingCompanies: [],
  setMatchingCompanies: () => {},
  setActiveDomainConfig: () => {},
  refreshToken: () => {}, // Add refresh token function to context
  getRememberMeStatus: () => {},
  getDurationText: () => {},
  showAuthIssueNotification: () => {},  // Add notification function to context
  clearAuthIssueNotifications: () => {}  // Add clear function to context
});

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginProgress, setLoginProgress] = useState(0);
  const [loginMessage, setLoginMessage] = useState('');
  const [currentDomain, setCurrentDomain] = useState('');
  const [activeDomainConfig, setActiveDomainConfig] = useState(null);
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [refreshInterval, setRefreshInterval] = useState(null);

  // Extract current domain on mount
  useEffect(() => {
    const hostname = window.location.hostname;
    setCurrentDomain(hostname);
  }, []);

  // Add state to track refresh operations
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  
  // Add state to track notification intervals for auth issues
  const [authIssueNotificationInterval, setAuthIssueNotificationInterval] = useState(null);
  const [lastAuthIssueNotification, setLastAuthIssueNotification] = useState(0);

  // Function to show authentication issue notification
  const showAuthIssueNotification = useCallback((message, type = 'warning') => {
    const now = Date.now();
    
    // Only show notification if 5 minutes have passed since last one
    if (now - lastAuthIssueNotification >= 300000) { // 5 minutes = 300000ms
      setLastAuthIssueNotification(now);
      
      // Create a notification element or use your notification system
      console.warn(`AUTH ISSUE (${type}): ${message}`);
      
      // You can replace this with your actual notification system
      // For example: toast.warning(message) or dispatch notification action
      if (window.showNotification) {
        window.showNotification(message, type);
      } else {
        // Fallback to browser notification if available
        if (Notification.permission === 'granted') {
          new Notification('Authentication Issue', {
            body: message,
            icon: '/favicon.ico'
          });
        }
      }
    }
  }, [lastAuthIssueNotification]);

  // Function to clear auth issue notifications
  const clearAuthIssueNotifications = useCallback(() => {
    if (authIssueNotificationInterval) {
      clearInterval(authIssueNotificationInterval);
      setAuthIssueNotificationInterval(null);
    }
    setLastAuthIssueNotification(0);
  }, [authIssueNotificationInterval]);
  const refreshTokenProactively = useCallback(async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!token) {
      console.log('No access token available');
      return false;
    }

    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing) {
      console.log('Token refresh already in progress, skipping...');
      return false;
    }

    // Prevent too frequent refresh attempts (minimum 30 seconds between attempts)
    const now = Date.now();
    if (now - lastRefreshTime < 30000) {
      console.log('Too soon since last refresh attempt, skipping...');
      return false;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decodedToken.exp - currentTime;
      
      // Only refresh if token expires in less than 2 minutes but more than 30 seconds
      if (timeUntilExpiry <= 120 && timeUntilExpiry > 30) {
        setIsRefreshing(true);
        setLastRefreshTime(now);
        console.log(`Proactively refreshing token (expires in ${Math.round(timeUntilExpiry)} seconds)...`);
        
        const response = await api.post(
          '/auth/refresh',
          refreshToken ? { refreshToken } : {}, // cookie-only when no legacy token
          { withCredentials: true }
        );
        
        localStorage.setItem('token', response.data.token);
        localStorage.removeItem('refreshToken');
        console.log('Token refreshed successfully');
        
        // Update refresh interval based on new token expiration
        updateRefreshInterval(response.data.token);
        
        return true;
      } else if (timeUntilExpiry <= 30) {
        // Token expires too soon, force logout
        console.log('Token expires too soon, forcing logout...');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setActiveDomainConfig(null);
        navigate('/');
        return false;
      }
    } catch (error) {
      console.error('Error in proactive token refresh:', error);
      // Case 2: Instead of forcing logout, show notification every 5 minutes
      showAuthIssueNotification(
        'Unable to refresh authentication token. Please check your connection and consider re-logging if issues persist.',
        'error'
      );
      return false;
    } finally {
      setIsRefreshing(false);
    }
    return false;
  }, [navigate, setUser, setActiveDomainConfig, isRefreshing, lastRefreshTime]);

  // Function to calculate refresh interval based on token expiration with better resource management
  const calculateRefreshInterval = (token) => {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const tokenLifetime = decodedToken.exp - decodedToken.iat;
      const timeUntilExpiry = decodedToken.exp - currentTime;
      
      // If token expires in less than 5 minutes, don't set up interval
      if (timeUntilExpiry < 300) {
        console.log('Token expires soon, not setting refresh interval');
        return null;
      }
      
      // Calculate refresh time to be 2 minutes before expiry, but not less than 5 minutes from now
      const refreshTime = Math.max(300, timeUntilExpiry - 120);
      
      console.log(`Token expires in ${Math.round(timeUntilExpiry)} seconds, refresh scheduled in ${Math.round(refreshTime)} seconds`);
      return refreshTime * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Error calculating refresh interval:', error);
      // Default to 14 minutes for safety
      return 14 * 60 * 1000;
    }
  };

  // Function to update refresh interval based on new token with better resource management
  const updateRefreshInterval = useCallback((newToken) => {
    // Clear existing interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
    
    // Calculate new interval
    const interval = calculateRefreshInterval(newToken);
    
    // Don't set interval if token expires too soon
    if (!interval) {
      console.log('Token expires too soon, not setting refresh interval');
      return;
    }
    
    const minutes = Math.floor(interval / (60 * 1000));
    
    // Set new interval with a single timeout instead of repeating interval
    const timeoutId = setTimeout(() => {
      refreshTokenProactively();
    }, interval);
    
    setRefreshInterval(timeoutId);
    console.log(`Token refresh scheduled in ${minutes} minutes`);
  }, [refreshInterval, refreshTokenProactively]);

  // Setup automatic token refresh interval with better resource management
  useEffect(() => {
    const startTokenRefreshInterval = () => {
      // Clear any existing interval
      if (refreshInterval) {
        clearTimeout(refreshInterval);
        setRefreshInterval(null);
      }

      // Only start interval if user is logged in
      const token = localStorage.getItem('token');
      if (token && user && !loading) {
        // Calculate appropriate refresh interval based on token expiration
        const interval = calculateRefreshInterval(token);
        
        if (interval) {
          const minutes = Math.floor(interval / (60 * 1000));
          
          const timeoutId = setTimeout(() => {
            refreshTokenProactively();
          }, interval);

          setRefreshInterval(timeoutId);
          console.log(`Token refresh scheduled in ${minutes} minutes`);
        }
      }
    };

    // Only start if we have a user and not loading
    if (user && !loading) {
      startTokenRefreshInterval();
    }

    // Cleanup interval when user logs out or component unmounts
    return () => {
      if (refreshInterval) {
        clearTimeout(refreshInterval);
        console.log('Token refresh timeout cleared');
      }
    };
  }, [user, loading]); // Removed refreshInterval and refreshTokenProactively from dependencies to prevent loops

  // Handle page visibility change to refresh token when user returns with better resource management
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user && !isRefreshing) {
        // User has returned to the tab, check if token needs refresh
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = decodedToken.exp - currentTime;
            
            // If token expires in less than 5 minutes, refresh it
            if (timeUntilExpiry <= 300 && timeUntilExpiry > 30) {
              console.log('Refreshing token after page visibility change');
              refreshTokenProactively();
            }
          } catch (error) {
            console.error('Error checking token on visibility change:', error);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, isRefreshing]); // Removed refreshTokenProactively from dependencies to prevent recreation

  // Function to get user-friendly duration text
  const getDurationText = (duration) => {
    const durationMap = {
      '1d': '1 Day',
      '3d': '3 Days', 
      '7d': '1 Week',
      '14d': '2 Weeks',
      '30d': '1 Month',
      '60d': '2 Months',
      '90d': '3 Months',
      '15m': '15 Minutes'
    };
    return durationMap[duration] || duration;
  };

  // Function to check if user has remember me enabled
  const getRememberMeStatus = () => {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const duration = localStorage.getItem('rememberDuration');
    return {
      enabled: rememberMe,
      duration: duration,
      durationText: getDurationText(duration)
    };
  };

  // Store interceptor ID to prevent multiple registrations
  const [interceptorRegistered, setInterceptorRegistered] = useState(false);

  // API interceptor with better resource management
  useEffect(() => {
    if (interceptorRegistered) return;

    const interceptorId = api.interceptors.request.use(async (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          const isExpired = decodedToken.exp < Date.now() / 1000;
          const timeUntilExpiry = decodedToken.exp - (Date.now() / 1000);

          if (isExpired || timeUntilExpiry < 30) {
            // Token is expired or expires very soon
            if (!isRefreshing) {
              try {
                setIsRefreshing(true);
                console.log('Token expired/expiring, refreshing via interceptor...');
                
                const refreshToken = localStorage.getItem('refreshToken');
                const response = await api.post(
                  '/auth/refresh',
                  refreshToken ? { refreshToken } : {}, // cookie-only when no legacy token
                  { withCredentials: true }
                );
                localStorage.setItem('token', response.data.token);
        localStorage.removeItem('refreshToken');
                config.headers.Authorization = `Bearer ${response.data.token}`;
                console.log('Token refreshed via interceptor');
                
                // Update refresh interval
                updateRefreshInterval(response.data.token);
              } catch (error) {
                console.log('Error refreshing token via interceptor:', error);
                // Case 3: Instead of forcing logout, show notification every 5 minutes
                showAuthIssueNotification(
                  'Authentication token refresh failed during API request. Some features may not work properly.',
                  'error'
                );
                // Clear refresh interval on token refresh failure
                if (refreshInterval) {
                  clearTimeout(refreshInterval);
                  setRefreshInterval(null);
                }
                return Promise.reject(error);
              } finally {
                setIsRefreshing(false);
              }
            } else {
              // Another refresh is in progress, wait for it
              return Promise.reject(new Error('Token refresh in progress'));
            }
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error decoding token:', error);
          // Case 4: Instead of forcing logout, show notification every 5 minutes
          showAuthIssueNotification(
            'Authentication token appears to be corrupted. Please try refreshing the page or re-login if issues persist.',
            'error'
          );
          // Clear refresh interval on token decode failure
          if (refreshInterval) {
            clearTimeout(refreshInterval);
            setRefreshInterval(null);
          }
          return Promise.reject(error);
        }
      }
      return config;
    });

    setInterceptorRegistered(true);

    // Cleanup function to remove interceptor
    return () => {
      if (interceptorId !== undefined) {
        api.interceptors.request.eject(interceptorId);
      }
    };
  }, [interceptorRegistered, isRefreshing, refreshInterval]); // Added missing dependencies

  // Enhanced function to find matching domain configuration
  const findMatchingDomainConfig = (domains = [], hostname) => {
    if (!domains || !domains.length || !hostname) return null;
    
    // First try exact match
    const exactMatch = domains.find(d => d.domainName === hostname);
    if (exactMatch) return exactMatch;

    // Then try includes match
    return domains.find(d => 
      hostname.includes(d.domainName) || 
      d.domainName.includes(hostname)
    );
  };

  // Function to find all matching companies for a domain
  const findAllMatchingCompanies = (companies = [], hostname) => {
    if (!companies || !companies.length || !hostname) return [];
    
    return companies.filter(company => {
      if (!company.domains) return false;
      return findMatchingDomainConfig(company.domains, hostname) !== null;
    });
  };

  // Update the processUserDomain function to handle CompanyHead properly
  const processUserDomain = (userObj, domain, role) => {
    // For SuperAdmin
    if (role === 'SuperAdmin') {
      return {
        user: userObj,
        domainConfig: { 
          access: true,
          adminDashboardAccess: ['dashboard'],
          domainName: domain || 'default'
        }
      };
    }

    // For Admin
    if (role === 'Admin') {
      // Admin processUserDomain logic remains the same...
      const firstDomain = userObj?.company?.domains?.[0] || {
        domainName: domain || 'default',
        adminDashboardAccess: ['dashboard'],
        companyHeadDashboardAccess: [],
        employeeDashboardAccess: []
      };

      return {
        user: {
          ...userObj,
          company: {
            ...userObj.company,
            currentDomain: firstDomain.domainName,
            adminDashboardAccess: firstDomain.adminDashboardAccess || ['dashboard'],
            companyHeadDashboardAccess: firstDomain.companyHeadDashboardAccess || [],
            employeeDashboardAccess: firstDomain.employeeDashboardAccess || []
          }
        },
        domainConfig: firstDomain
      };
    }

    // For CompanyHead - need to handle even when company is just an ID
    if (role === 'CompanyHead') {
      // If company is missing or just an ID reference, return temporary access
      // The CompanyHeadDashboard will handle the full company fetch
      if (!userObj.company || typeof userObj.company === 'string' || !userObj.company.domains) {
        return {
          user: userObj,
          domainConfig: {
            domainName: domain || 'default',
            companyHeadDashboardAccess: ['dashboard'],
            adminDashboardAccess: [],
            employeeDashboardAccess: []
          }
        };
      }

      // If company has domains, try to find a match
      if (userObj.company.domains && userObj.company.domains.length > 0) {
        const matchingDomain = findMatchingDomainConfig(userObj.company.domains, domain);
        
        // Use matching domain if found
        if (matchingDomain) {
          return {
            user: {
              ...userObj,
              company: {
                ...userObj.company,
                currentDomain: matchingDomain.domainName,
                adminDashboardAccess: matchingDomain.adminDashboardAccess || [],
                companyHeadDashboardAccess: matchingDomain.companyHeadDashboardAccess || ['dashboard'],
                employeeDashboardAccess: matchingDomain.employeeDashboardAccess || []
              }
            },
            domainConfig: matchingDomain
          };
        }
        
        // If no matching domain, use first domain as fallback
        const firstDomain = userObj.company.domains[0];
        return {
          user: {
            ...userObj,
            company: {
              ...userObj.company,
              currentDomain: firstDomain.domainName,
              adminDashboardAccess: firstDomain.adminDashboardAccess || [],
              companyHeadDashboardAccess: firstDomain.companyHeadDashboardAccess || ['dashboard'],
              employeeDashboardAccess: firstDomain.employeeDashboardAccess || []
            }
          },
          domainConfig: firstDomain
        };
      }
    }

    // For Employee or any other role
    if (!userObj?.company?.domains || userObj.company.domains.length === 0) {
      return { 
        user: userObj, 
        domainConfig: null 
      };
    }

    // Generic domain matching for other cases
    const matchingDomain = findMatchingDomainConfig(userObj.company.domains, domain);
    
    if (matchingDomain) {
      return {
        user: {
          ...userObj,
          company: {
            ...userObj.company,
            currentDomain: matchingDomain.domainName,
            adminDashboardAccess: matchingDomain.adminDashboardAccess || [],
            companyHeadDashboardAccess: matchingDomain.companyHeadDashboardAccess || [],
            employeeDashboardAccess: matchingDomain.employeeDashboardAccess || []
          }
        },
        domainConfig: matchingDomain
      };
    }

    // Default to first domain as fallback
    const firstDomain = userObj.company.domains[0];
    return {
      user: {
        ...userObj,
        company: {
          ...userObj.company,
          currentDomain: firstDomain.domainName,
          adminDashboardAccess: firstDomain.adminDashboardAccess || [],
          companyHeadDashboardAccess: firstDomain.companyHeadDashboardAccess || [],
          employeeDashboardAccess: firstDomain.employeeDashboardAccess || []
        }
      },
      domainConfig: firstDomain
    };
  };

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          if (decodedToken.exp < Date.now() / 1000) {
            try {
              const refreshToken = localStorage.getItem('refreshToken');
              const response = await api.post(
                '/auth/refresh',
                refreshToken ? { refreshToken } : {}, // cookie-only when no legacy token
                { withCredentials: true }
              );
              localStorage.setItem('token', response.data.token);
        localStorage.removeItem('refreshToken');
              
              // Process user with domain-specific permissions
              const { user: userWithDomainAccess, domainConfig } = 
                processUserDomain(decodedToken.user, currentDomain, decodedToken.role);
              
              setUser({
                role: decodedToken.role,
                user: userWithDomainAccess
              });
              
              setActiveDomainConfig(domainConfig);
            } catch (error) {
              console.log('Error checking token:', error);
              // Case 5: Instead of forcing logout, show notification every 5 minutes
              showAuthIssueNotification(
                'Failed to refresh expired authentication token. Please try refreshing the page or re-login.',
                'error'
              );
            }
          } else {
            // Process user with domain-specific permissions
            const { user: userWithDomainAccess, domainConfig } = 
              processUserDomain(decodedToken.user, currentDomain, decodedToken.role);
            
            setUser({
              role: decodedToken.role,
              user: userWithDomainAccess
            });
            
            setActiveDomainConfig(domainConfig);
          }
        } catch (error) {
          console.error('Error processing token:', error);
          localStorage.removeItem('token');
          setUser(null);
          setActiveDomainConfig(null);
        }
      }
      setLoading(false);
    };
    
    // Only run when we have a current domain and haven't processed the token yet
    if (currentDomain && loading) {
      checkToken();
    }
  }, [currentDomain]); // Keep dependency but add loading guard

  // Add this function to verify domain access
  const verifyDomainAccess = async (userId, role, domain) => {
    if (role === 'SuperAdmin') return true; // SuperAdmin always has access
    
    try {
      const response = await api.get('/companies/verify-domain-access', {
        params: { userId, role, domain }
      });
      return response.data.hasAccess;
    } catch (error) {
      console.error('Error verifying domain access:', error);
      return false;
    }
  };
  // Update the login function
  const login = async (email, password, rememberDuration = null) => {
    setLoginLoading(true);
    setLoginProgress(0);
    setLoginMessage('Authenticating...');
    
    try {
      setLoginProgress(20);
      const requestBody = { email, password };
      
      // Add remember me duration if specified
      if (rememberDuration) {
        requestBody.rememberDuration = rememberDuration;
      }
      
      const response = await api.post("/auth/login", requestBody);
      
      // Log the response for debugging
      console.log('Login response received:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : []
      });
      
      // Validate response structure
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      const { token, role, user: userData } = response.data;
      
      // Validate required fields
      if (!token || !role || !userData) {
        console.error('Invalid response structure:', response.data);
        throw new Error('Invalid response from server');
      }
      
      setLoginProgress(40);
      setLoginMessage('Validating credentials...');
      localStorage.setItem('token', token);
      
      // Refresh token is delivered in an HttpOnly cookie. Do not persist it in
      // localStorage for new logins; keep any legacy value only as fallback.
      localStorage.removeItem('refreshToken');
      
      // Store remember me preferences if provided
      if (response.data.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberDuration', response.data.duration);
        console.log(`Remember me enabled for ${response.data.duration}`);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberDuration');
      }
      
      setLoginProgress(60);
      setLoginMessage('Processing user data...');
      
      // Check user verification status for all roles
      const isUserVerified = userData.isVerified !== false; // Default to true if field doesn't exist
      
      // Handle SuperAdmin (no change)
      if (role === 'SuperAdmin') {
        setLoginProgress(100);
        setLoginMessage('Welcome SuperAdmin!');
        
        setUser({ role, user: userData });
        setActiveDomainConfig(null);
        
        setLoginLoading(false);
        setLoginProgress(0);
        setLoginMessage('');
        
        return { 
          role,
          error: null,
          domainConfig: { access: true },
          domainMatch: true,
          isVerified: isUserVerified,
          userEmail: userData.email
        };
      }
      
      setLoginProgress(70);
      setLoginMessage('Checking domain access...');
      
      // Handle Admin with improved domain handling  
      if (role === 'Admin') {
        try {
          setLoginMessage('Loading admin dashboard...');
          let hasValidDomain = false;
          let adminDashboardAccess = ['dashboard']; // Default access
          let matchingCompany = null;
          let matchingDomain = null;
          
          // If admin has company data already
          if (userData.company && typeof userData.company === 'object' && userData.company.domains) {
            matchingDomain = findMatchingDomainConfig(userData.company.domains, currentDomain);
            hasValidDomain = !!matchingDomain;
            if (matchingDomain) {
              adminDashboardAccess = matchingDomain.adminDashboardAccess || ['dashboard'];
            }
          } 
          // If admin doesn't have company data or just has ID
          else {
            // Fetch all companies
            try {
              setLoginProgress(80);
              const companiesResponse = await api.get('/companies');
              const companies = companiesResponse.data;
              
              // Find company with matching domain
              for (const company of companies) {
                if (company.domains && company.domains.length > 0) {
                  const domainConfig = findMatchingDomainConfig(company.domains, currentDomain);
                  if (domainConfig) {
                    matchingCompany = company;
                    matchingDomain = domainConfig;
                    adminDashboardAccess = domainConfig.adminDashboardAccess || ['dashboard'];
                    hasValidDomain = true;
                    break;
                  }
                }
              }
            } catch (companyError) {
              console.error("Error fetching companies:", companyError);
            }
          }
          
          setLoginProgress(90);
          setLoginMessage('Finalizing login...');
          
          // Process the user with found domain info
          const processedUser = {
            ...userData,
            company: matchingCompany || userData.company || {},
            adminDashboardAccess
          };
          
          setUser({
            role,
            user: processedUser
          });
          
          setActiveDomainConfig(matchingDomain);
          
          setLoginProgress(100);
          setLoginMessage('Welcome Admin!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return {
            role,
            error: null,
            domainConfig: matchingDomain,
            domainMatch: hasValidDomain,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        } catch (error) {
          console.error("Error processing admin login:", error);
          
          // Basic fallback
          setUser({
            role,
            user: {
              ...userData,
              adminDashboardAccess: ['dashboard']
            }
          });
          
          setActiveDomainConfig({ 
            domainName: currentDomain,
            adminDashboardAccess: ['dashboard', 'campaign-management', 'campaign-reports', 'schemes-reports']
          });
          
          setLoginProgress(100);
          setLoginMessage('Welcome Admin!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return {
            role,
            error: null,
            domainConfig: {
              domainName: currentDomain,
              adminDashboardAccess: ['dashboard', 'campaign-management', 'campaign-reports', 'schemes-reports']
            },
            domainMatch: false,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        }
      }
      
      // Handle CompanyHead with improved domain handling
      if (role === 'CompanyHead') {
        try {
          setLoginMessage('Loading company data...');
          let companyData;
          let hasValidDomain = false;
          
          setLoginProgress(80);
          // Fetch company data if we have a company ID
          if (userData.company) {
            const companyId = userData.company._id || userData.company;
            
            try {
              const companyResponse = await api.get(`/companies/${companyId}`);
              companyData = companyResponse.data;
              
              // Explicitly check domain access
              if (companyData && companyData.domains) {
                // Check if any domain matches the current domain
                const matchingDomain = findMatchingDomainConfig(companyData.domains, currentDomain);
                hasValidDomain = !!matchingDomain;
                
                // Update user data with fetched company
                userData.company = companyData;
              }
            } catch (companyError) {
              console.error("Error fetching company data:", companyError);
              // Continue with original company data
            }
          }
          
          setLoginProgress(90);
          setLoginMessage('Verifying access...');
          
          // If no valid domain found, verify with the backend
          if (!hasValidDomain) {
            const accessCheck = await verifyDomainAccess(
              userData._id,
              role,
              currentDomain
            );
            
            if (!accessCheck) {
              // Show warning but still return user for the CompanyHeadDashboard to handle
              console.warn("Company Head doesn't have access to this domain:", currentDomain);
            }
          }
          
          // Process domain access          const { user: processedUser, domainConfig } = 
            processUserDomain(userData, currentDomain, role);
          
          setUser({ 
            role, 
            user: processedUser 
          });
          
          setActiveDomainConfig(domainConfig);
          
          setLoginProgress(100);
          setLoginMessage('Welcome Company Head!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return { 
            role,
            error: null,
            domainConfig,
            domainMatch: hasValidDomain,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        } catch (error) {
          console.error("Error processing company head login:", error);
          
          // Even if there's an error, try to provide basic access
          const { user: processedUser, domainConfig } = 
            processUserDomain(userData, currentDomain, role);
          
          setUser({ 
            role, 
            user: processedUser 
          });
          
          setActiveDomainConfig(domainConfig);
          
          setLoginProgress(100);
          setLoginMessage('Welcome Company Head!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return { 
            role,
            error: null,
            domainConfig,
            domainMatch: false,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        }
      }

      // Handle Employee with domain validation
      if (role === 'Employee') {
        try {
          setLoginMessage('Loading employee data...');
          let companyData;
          let hasValidDomain = false;
          
          setLoginProgress(80);
          // Fetch company data if we have a company ID
          if (userData.company) {
            const companyId = userData.company._id || userData.company;
            
            try {
              const companyResponse = await api.get(`/companies/${companyId}`);
              companyData = companyResponse.data;
              
              // Check domain access
              if (companyData && companyData.domains) {
                const matchingDomain = findMatchingDomainConfig(companyData.domains, currentDomain);
                hasValidDomain = !!matchingDomain;
                
                // Update user data with fetched company
                userData.company = companyData;
              }
            } catch (companyError) {
              console.error("Error fetching company data for employee:", companyError);
            }
          }
          
          setLoginProgress(90);
          setLoginMessage('Verifying employee access...');
          
          // Verify domain access with backend if needed
          if (!hasValidDomain) {
            const accessCheck = await verifyDomainAccess(
              userData._id,
              role,
              currentDomain
            );
            
            if (!accessCheck) {
              console.warn("Employee doesn't have access to this domain:", currentDomain);
            }
          }
          
          // Process domain access
          const { user: processedUser, domainConfig } = 
            processUserDomain(userData, currentDomain, role);
          
          setUser({ 
            role, 
            user: processedUser 
          });
          
          setActiveDomainConfig(domainConfig);
          
          setLoginProgress(100);
          setLoginMessage('Welcome Employee!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return { 
            role,
            error: null,
            domainConfig,
            domainMatch: hasValidDomain,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        } catch (error) {
          console.error("Error processing employee login:", error);
          
          // Provide basic access even with error
          const { user: processedUser, domainConfig } = 
            processUserDomain(userData, currentDomain, role);
          
          setUser({ 
            role, 
            user: processedUser 
          });
          
          setActiveDomainConfig(domainConfig);
          
          setLoginProgress(100);
          setLoginMessage('Welcome Employee!');
          
            setLoginLoading(false);
          setLoginProgress(0);
          setLoginMessage('');
          
          return { 
            role,
            error: null,
            domainConfig,
            domainMatch: false,
            isVerified: isUserVerified,
            userEmail: userData.email
          };
        }
      }

      // For other roles (fallback)
      setLoginProgress(90);
      setLoginMessage('Processing login...');
      
      const { user: processedUser, domainConfig } = processUserDomain(userData, currentDomain, role);
      
      setUser({ 
        role, 
        user: processedUser 
      });
      setActiveDomainConfig(domainConfig);
      
      setLoginProgress(100);
      setLoginMessage('Login successful!');
      
      setLoginLoading(false);
      setLoginProgress(0);
      setLoginMessage('');
      
      return { 
        role,
        error: null,
        domainConfig,
        domainMatch: !!domainConfig,
        isVerified: isUserVerified,
        userEmail: userData.email
      };
    } catch (error) {
      console.error("Login error:", error);
      setLoginLoading(false);
      setLoginProgress(0);
      setLoginMessage('');
      
      // Check if it's an email verification error
      if (error.response?.status === 403 && error.response?.data?.needsVerification) {
        return {
          error: error.response.data.msg || 'Email verification required',
          needsVerification: true,
          email: error.response.data.email,
          loginCount: error.response.data.loginCount,
          loginLimit: error.response.data.loginLimit,
          role: null,
          domainConfig: null,
          domainMatch: false
        };
      }
      
      return {
        error: error.response?.data?.message || error.response?.data?.msg || 'Login failed',
        role: null,
        domainConfig: null,
        domainMatch: false
      };
    }
  };

  // Update the autoLogin function
  const autoLogin = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp > Date.now() / 1000) {
          const role = decodedToken.role;
          
          // SuperAdmin doesn't need domain validation
          if (role === 'SuperAdmin') {
            setUser({
              role,
              user: decodedToken.user
            });
            setActiveDomainConfig(null);
            
            if(window.location.pathname === '/') {
              setTimeout(() => {
                navigate('/super-admin/dashboard');
              }, 1000);
            }
            return;
          }

          // For Admin, fetch company data but skip domain validation
          if (role === 'Admin') {
            try {
              // Enhanced handling for company data similar to AdminDashboard.js
              if (!decodedToken.user.company || 
                  typeof decodedToken.user.company === 'string' || 
                  !decodedToken.user.company.domains || 
                  decodedToken.user.company.domains.length === 0) {
                
                // Try to fetch specific company if ID is available
                if (decodedToken.user.company) {
                  const companyId = decodedToken.user.company._id || decodedToken.user.company;
                  
                  try {
                    const companyResponse = await api.get(`/companies/${companyId}`);
                    const companyData = companyResponse.data;
                    
                    if (companyData) {
                      const matchingDomain = findMatchingDomainConfig(companyData.domains, currentDomain);
                      if (matchingDomain) {
                        const updatedUserData = {
                          ...decodedToken.user,
                          company: companyData
                        };
                        
                        setUser({
                          role,
                          user: updatedUserData
                        });
                        setActiveDomainConfig(matchingDomain);
                        
                        if(window.location.pathname === '/') {
                          setTimeout(() => {
                            navigate('/admin/dashboard');
                          }, 1000);
                        }
                        setLoading(false);
                        return;
                      }
                    }
                  } catch (err) {
                    console.error('Error fetching specific company:', err);
                  }
                }
                
                // If specific company failed or no match found, try all companies
                try {
                  const response = await api.get('/companies');
                  const companies = response.data;
                  
                  // Find all companies with matching domains
                  const matchingCompaniesFound = findAllMatchingCompanies(companies, currentDomain);
                  
                  // Store matching companies in state and localStorage
                  if (matchingCompaniesFound && matchingCompaniesFound.length > 0) {
                    setMatchingCompanies(matchingCompaniesFound);
                    localStorage.setItem('matchingCompanies', JSON.stringify(matchingCompaniesFound));
                    
                    // If only one match, use it directly
                    if (matchingCompaniesFound.length === 1) {
                      const company = matchingCompaniesFound[0];
                      const matchingDomain = findMatchingDomainConfig(company.domains, currentDomain);
                      
                      if (matchingDomain) {
                        const updatedUserData = {
                          ...decodedToken.user,
                          company: company
                        };
                        
                        setUser({
                          role,
                          user: updatedUserData
                        });
                        setActiveDomainConfig(matchingDomain);
                        
                        if(window.location.pathname === '/') {
                          setTimeout(() => {
                            navigate('/admin/dashboard');
                          }, 1000);
                        }
                        setLoading(false);
                        return;
                      }
                    } else {
                      // Multiple matches - select the first one for now
                      // The CampaignManagement component will allow selection
                      const company = matchingCompaniesFound[0];
                      const matchingDomain = findMatchingDomainConfig(company.domains, currentDomain);
                      
                      const updatedUserData = {
                        ...decodedToken.user,
                        company: company
                      };
                      
                      setUser({
                        role,
                        user: updatedUserData
                      });
                      setActiveDomainConfig(matchingDomain);
                      
                      if(window.location.pathname === '/') {
                        setTimeout(() => {
                          navigate('/admin/dashboard');
                        }, 1000);
                      }
                      setLoading(false);
                      return;
                    }
                  }
                } catch (err) {
                  console.error('Error fetching all companies:', err);
                }
              }
              
              // If we have complete company data already, just fetch to ensure it's up to date
              if (decodedToken.user.company) {
                try {
                  const companyResponse = await api.get(`/companies/${decodedToken.user.company._id || decodedToken.user.company}`);
                  const companyData = companyResponse.data;
                  
                  const updatedUserData = {
                    ...decodedToken.user,
                    company: companyData
                  };
                  
                  // Process user data but don't validate domain
                  const { user: userWithAccess, domainConfig } = 
                    processUserDomain(updatedUserData, currentDomain, role);
                  
                  setUser({
                    role,
                    user: userWithAccess
                  });
                  setActiveDomainConfig(domainConfig);
                } catch (error) {
                  console.error("Error fetching company data:", error);
                  // Set user without company data
                  setUser({
                    role,
                    user: decodedToken.user
                  });
                  setActiveDomainConfig({
                    access: true,
                    adminDashboardAccess: ['dashboard', 'campaign-management', 'campaign-reports', 'schemes-reports'],
                    domainName: currentDomain
                  });
                }
              } else {
                // For Admin without company, just set basic access
                setUser({
                  role,
                  user: decodedToken.user
                });
                setActiveDomainConfig({
                  access: true,
                  adminDashboardAccess: ['dashboard', 'campaign-management', 'campaign-reports', 'schemes-reports'],
                  domainName: currentDomain
                });
              }
              
              // Only navigate if on root path and not already navigating
              if(window.location.pathname === '/' && !window.location.hash) {
                setTimeout(() => {
                  navigate('/admin/dashboard');
                }, 100);
              }
            } catch (error) {
              console.error("Error fetching company data:", error);
              // Still allow admin to access with basic info
              setUser({
                role,
                user: decodedToken.user
              });
              setActiveDomainConfig({ access: true });
              
              // Only navigate if on root path and not already navigating
              if(window.location.pathname === '/' && !window.location.hash) {
                setTimeout(() => {
                  navigate('/admin/dashboard');
                }, 100);
              }
            }
            return;
          }
          
          // For CompanyHead and other roles
          const { user: userWithDomainAccess, domainConfig } = 
            processUserDomain(decodedToken.user, currentDomain, role);
          
          setUser({
            role,
            user: userWithDomainAccess
          });
          
          setActiveDomainConfig(domainConfig);
          
          // Only navigate if on root path and not already navigating
          if(window.location.pathname === '/' && !window.location.hash) {
            setTimeout(() => {
              switch(role) {
                case 'CompanyHead':
                  navigate('/company-head/dashboard');
                  break;
                case 'Employee':
                  navigate('/employee/dashboard');
                  break;
                default:
                  break;
              }
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error in autoLogin:', error);
        localStorage.removeItem('token');
        setUser(null);
        setActiveDomainConfig(null);
      }
    }
    setLoading(false);
  };

  // Enhanced logout function with better cleanup
  const logout = async () => {
    try {
      // Clear the token refresh interval/timeout
      if (refreshInterval) {
        clearTimeout(refreshInterval);
        setRefreshInterval(null);
      }
      
      // Clear auth issue notifications
      clearAuthIssueNotifications();
      
      // Reset refresh state
      setIsRefreshing(false);
      setLastRefreshTime(0);
      
      const legacyRefreshToken = localStorage.getItem("refreshToken");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken"); // Remove refresh token
      localStorage.removeItem("selectedCompanyID");
      localStorage.removeItem("matchingCompanies");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberDuration");
      setUser(null);
      setActiveDomainConfig(null);
      setMatchingCompanies([]);
      
      await api.post("/auth/logout", legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {}, {
        withCredentials: true,
      });
      console.log('Logout successful');
      navigate('/', { replace: true });
    } catch (error) {
      console.log('Logout error:', error);
      // Clear the token refresh interval even on error
      if (refreshInterval) {
        clearTimeout(refreshInterval);
        setRefreshInterval(null);
      }
      clearAuthIssueNotifications();
      setIsRefreshing(false);
      setLastRefreshTime(0);
      setUser(null);
      setActiveDomainConfig(null);
      setMatchingCompanies([]);
      localStorage.removeItem('token');
      localStorage.removeItem("selectedCompanyID");
      localStorage.removeItem("matchingCompanies");
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberDuration");
      navigate('/', { replace: true });
    }
  };

  // Load matching companies from localStorage on mount
  useEffect(() => {
    try {
      const storedMatchingCompanies = localStorage.getItem('matchingCompanies');
      if (storedMatchingCompanies) {
        const parsedCompanies = JSON.parse(storedMatchingCompanies);
        if (Array.isArray(parsedCompanies) && parsedCompanies.length > 0) {
          setMatchingCompanies(parsedCompanies);
        }
      }
    } catch (err) {
      console.error('Error loading stored matching companies:', err);
    }
  }, []);

  useEffect(() => {
    autoLogin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup auth issue notifications on unmount
  useEffect(() => {
    return () => {
      clearAuthIssueNotifications();
    };
  }, [clearAuthIssueNotifications]);
  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      loading, 
      loginLoading,
      loginProgress,
      loginMessage,
      currentDomain,
      activeDomainConfig,
      matchingCompanies,
      setMatchingCompanies,
      setActiveDomainConfig,  // Add this line to expose the setter
      refreshToken: refreshTokenProactively,  // Expose the refresh function
      getRememberMeStatus,
      getDurationText,
      showAuthIssueNotification,  // Expose notification function
      clearAuthIssueNotifications  // Expose clear function
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;